<template>
  <fieldset
    class="diary-phrase-library"
    :tabindex="-1"
    :style="!show ? 'display: none;' : ''"
  >
    <input-text-search
      v-if="phrases.length"
      v-bind:value="phraseQuery"
      v-on:input="phraseQuery = $event"
    />
    <template v-if="filteredPhrases.length">
      <div
        v-for="item in filteredPhrases"
        :key="item.key">
        <div
          v-if="item.withChildren"
          class="diary-phrase-library__group-container">
          <button
            v-bind:class="[
              item.type === 'group'
                ? 'diary-phrase-library__group'
                : 'diary-phrase-library__subgroup',
              expandedFolderKeys.includes(item.key) ? 'expanded' : '',
            ]"
            type="button"
            v-bind:title="'Переключить видимость фраз в папке'"
            v-on:click="toggleFolder(item)">
            <span class="diary-phrase-library__group-title">
              {{ item.title }}
            </span>
            <span class="diary-phrase-library__actions">
              <button
                class="diary-phrase-library__button-edit"
                type="button"
                title="Редактировать папку"
                v-on:click.stop="onEditFolder(item)" />
              <button
                class="diary-phrase-library__button-delete"
                type="button"
                title="Удалить папку"
                v-on:click.stop="onRemoveFolder(item)" />
              <span
                class="diary-phrase-library__arrow"
                v-bind:class="{ expanded: expandedFolderKeys.includes(item.key) }" />
            </span>
          </button>
        </div>
        <div
          v-else
          class="diary-phrase-library__buttons">
          <template v-if="!Array.isArray(item.value) || item.value.length < 2">
            <button
              v-bind:class="{
                'diary-phrase-library__phrase': true,
                selected: selectedPhrases.includes(item.key),
              }"
              type="button"
              v-bind:title="'Вставить фразу в поле протокола'"
              v-bind:style="`margin-left: ${getMarginLeft(item.key)}px`"
              v-on:click="onInputPhrase(item.key, item.value)"
            >
              {{ item.title }}
            </button>
          </template>
          <template v-if="item.id">
            <button
              class="diary-phrase-library__button-edit"
              type="button"
              title="Редактировать фразу"
              v-on:click="onEditPhrase(item.id, item.value, item.group)" />
            <button
              class="diary-phrase-library__button-delete"
              type="button"
              title="Удалить фразу"
              v-on:click="onRemovePhrase(item.id)" />
          </template>
        </div>
      </div>
    </template>
    <em v-else>Для поля нет подходящих фраз.</em>
    <div class="diary-phrase-library__actions diary-phrase-library__group-buttons-add">
      <button
        class="diary-phrase-library__button-add"
        type="button"
        title="Добавить новую фразу в библиотеку"
        v-on:click="onAddGroup">
        Добавить группу
      </button>
      <button
        class="diary-phrase-library__button-add"
        type="button"
        title="Добавить новую фразу в библиотеку"
        v-on:click="onAddPhrase">
        Добавить фразу
      </button>
    </div>
  </fieldset>
</template>

<script>
import InputTextSearch from '@/components/InputTextSearch.vue';

/**
 * @description Название события ввода фразы.
 * @type {string}
 */
const NAME_EVENT_INPUT_PHRASE = 'input-phrase';

/**
 * @description Название события удаления фразы.
 * @type {string}
 */
const NAME_EVENT_REMOVE_PHRASE = 'remove-phrase';

/**
 * @description Название события удаления группы фраз.
 * @type {string}
 */
const NAME_EVENT_REMOVE_FOLDER = 'remove-group';

/**
 * @description Название события редактирования фразы.
 * @type {string}
 */
const NAME_EVENT_EDIT_PHRASE = 'edit-phrase';

/**
 * @description Название события редактирования группы.
 * @type {string}
 */
const NAME_EVENT_EDIT_FOLDER = 'edit-group';

/**
 * @description Название события добавления фразы.
 * @type {string}
 */
const NAME_EVENT_ADD_PHRASE = 'add-phrase';

/**
 * @description Название события добавления группы.
 * @type {string}
 */
const NAME_EVENT_ADD_GROUP = 'add-group';

/**
 * @description Компонент библиотеки фраз.
 * @type {Vue}
 */
export default {
  components: {
    InputTextSearch,
  },
  computed: {
    /**
     * @description Отфильтрованные фразы по поиску.
     */
    filteredPhrases() {
      if (this.phraseQuery) {
        const query = this.phraseQuery.toLowerCase();
        const filterAndFlat = (items) => items.flatMap((item) => {
          if (item.withChildren) {
            return [...filterAndFlat(item.value)];
          }
          return item.value[0].toLowerCase().includes(query) ? [item] : [];
        });
        return filterAndFlat(this.phrases);
      }
      const expandAndFlat = (items) => items.flatMap((item) => {
        if (item.withChildren) {
          const isExpanded = this.expandedFolderKeys.includes(item.key);
          if (isExpanded) {
            return [
              { ...item, class: ['expanded'] },
              ...expandAndFlat(item.value),
            ];
          }
        }
        return [{ ...item, class: [] }];
      });
      return expandAndFlat(this.phrases);
    },
  },
  data() {
    return {
      expandedFolderKeys: [],
      phraseQuery: '',
      selectedPhrases: [],
    };
  },
  methods: {
    /**
     * @description Получить отступ слева.
     * @returns {number}
     */
    getMarginLeft(key) {
      return (key.match(/subgroup/g) || []).length * 34 + 14;
    },
    /**
     * @description Обработать ввод фразы.
     */
    onInputPhrase(key, value) {
      this.selectedPhrases.push(key);
      this.$emit(NAME_EVENT_INPUT_PHRASE, value);
    },
    /**
     * @description Обработать событие добавления фразы.
     */
    onAddPhrase() {
      this.$emit(NAME_EVENT_ADD_PHRASE);
    },
    /**
     * @description Обработать событие добавления группы.
     */
    onAddGroup() {
      this.$emit(NAME_EVENT_ADD_GROUP);
    },
    /**
     * @description Обработать событие удаления фразы.
     * @param phraseId
     */
    onRemovePhrase(phraseId) {
      this.$emit(NAME_EVENT_REMOVE_PHRASE, phraseId);
    },
    /**
     * @description Обработать событие редактирования фразы.
     */
    onEditPhrase(phraseId, phrase, groupId) {
      this.$emit(NAME_EVENT_EDIT_PHRASE, phraseId, ...phrase, groupId);
    },
    /**
     * @description Обработать событие редактирования группы фраз.
     */
    onEditFolder(group) {
      this.$emit(NAME_EVENT_EDIT_FOLDER, group.id, group.title);
    },
    /**
     * @description Обработать событие удаления группы фраз.
     */
    onRemoveFolder(group) {
      this.$emit(NAME_EVENT_REMOVE_FOLDER, group.id);
    },
    /**
     * @description Переключение видимости отображения группы.
     */
    toggleFolder(item) {
      const filteredKeys = this.expandedFolderKeys.filter((key) => key !== item.key);
      const isNotExpanded = filteredKeys.length === this.expandedFolderKeys.length;
      if (item.type === 'group') {
        this.expandedFolderKeys = isNotExpanded ? [item.key] : filteredKeys;
      } else {
        if (isNotExpanded) {
          filteredKeys.push(item.key);
        }
        this.expandedFolderKeys = filteredKeys;
      }
    },
  },
  name: 'PhraseLibrary',
  props: {
    phrases: {
      type: Array,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.menuable__content__active {
  min-width: auto !important;
}

.diary-phrase-library {
  overflow-y: scroll;
  position: sticky;
  top: 50px;
  background-color: var(--primary-background-color);
  border: none;
  max-height: 80vh;
  min-height: 300px;
  height: fit-content;
  counter-reset: counter;
  grid-area: library;
  @media screen and (max-width: 600px) {
    max-height: inherit;
    min-height: inherit;
    top: inherit;
    position: relative;
  }
  .v-input {
    position: sticky;
    top: -0.35rem;
    background: white;
    margin: 0 0 39px 0;
    z-index: 1;
  }
  &__actions-menu {
    margin-right: 10px;
    padding-inline: 5px;
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
  }
  &__group-buttons-add {
    padding-inline: 14px;
    @media screen and (max-width: 400px) {
      display: grid;
      button {
        max-width: none;
        margin: 0;
      }
    }
  }
  &__group-title {
    text-align: left;
    flex-grow: 1;
  }
  &__group {
    display: flex;
    align-items: center;
    padding: 5px 14px;
    font-weight: 500;
    color: #323C47;
    font-size: 18px;
    font-style: normal;
    width: 100%;
    &:before {
      counter-increment: counter;
      content: counter(counter) ". ";
      margin: 0 20px 0 0;
    }
    &:after {
      content: '';
      background: url("@/assets/img/checked/arrow-top-blue.svg") no-repeat 50%;
      margin: 0 0 0 auto;
      width: 20px;
      height: 20px;
    }
    &.expanded {
      color: #0489E0;
    }
    &.expanded::after {
      transform: rotate(-180deg);
    }
    @media screen and (min-width: 768px) {
      padding-right: 0;
    }
  }
  &__subgroup {
    display: flex;
    align-items: center;
    text-align: left;
    padding: 5px 14px;
    font-weight: 500;
    color: #323C47;
    font-size: 18px;
    font-style: normal;
    width: 100%;
    & > span:first-child {
      margin-left: 34px;
    }
    &:after {
      content: '';
      background: url("@/assets/img/checked/arrow-top-blue.svg") no-repeat 50%;
      margin: 0 0 0 auto;
      width: 20px;
      height: 20px;

    }
    &.expanded {
      color: #0489E0;
    }
    &.expanded::after {
      transform: rotate(-180deg);
    }
    @media screen and (min-width: 768px) {
      padding-right: 0;
    }
  }
  &__phrase {
    display: flex;
    align-items: center;
    text-align: left;
    word-break: break-word;
    padding: 7px 14px;
    font-size: 14px;
    border-radius: 3px;
    background: #EAEFF3;
    color: #323C47;
    transition: all 0.3s ease 0s;
    cursor: pointer;
    margin-top: 5px;
    &:hover {
      background-color: #0076C4;
      color: #FFF;
      transition: all 0.3s ease 0s;
    }
    &.selected {
      background-color: #0076C4;
      color: #FFF;
    }
    &+.diary-phrase-library__group {
      margin: 20px 0 0 0;
    }
  }
  &__dropdown {
    display: flex;
    align-items: center;
    width: 100%;
    select {
      border: 1px solid #c2cfe0;
      outline: none;
      background-color: transparent;
      width: 100%;
      &:hover {
        background-color: transparent;
        color: #192a3e;
      }
    }
    &:after {
      position: relative;
      top: 7px;
      right: 25px;
      content: '';
      background-image: url("@/assets/img/checked/arrow-top-blue.svg");
      background-repeat: no-repeat;
      width: 20px;
      height: 20px;
    }
    &:focus {
      color: #192a3e;
      background-color: #fff;
      border: 1px solid #0489e0;
      outline: 0;
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: auto 12px 15px;
    align-items: center;
    grid-gap: 10px;
  }
  &__button {
    &-add {
      @include buttons.btn-transparent(100%, 48px, 16px);
      margin: 20px 0 0 0;
      max-width: 174px;
    }
    &-group-add {
      display: inline-flex;
      background-size: contain;
      background-repeat: no-repeat;
      width: 12px;
      height: 12px;
      cursor: pointer;
      transform: rotate(45deg);
      background-image: url("@/assets/img/exit-grey.svg");
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
        transform: rotate(45deg);
      }
    }
    &-delete {
      display: inline-flex;
      background-size: contain;
      background-repeat: no-repeat;
      width: 12px;
      height: 12px;
      cursor: pointer;
      background-image: url("@/assets/img/exit-grey.svg");
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
    &-edit {
      display: inline-flex;
      background-image: url("@/assets/img/pen-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      width: 15px;
      min-width: 15px;
      height: 15px;
      min-height: 15px;
      padding: 0;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
    &-vertical-dots {
      display: inline-flex;
      background-image: url("@/assets/img/points.svg");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      width: 15px;
      min-width: 15px;
      height: 15px;
      min-height: 15px;
      padding: 0;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/points-blue.svg");
      }
    }
  }
}
</style>
